// src/components/ErrorState.js

// src/components/ErrorState.js

import React from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    useTheme,
    Avatar,
    Zoom,
    Fade,
    Stack,
    Chip,
    Divider,
    Alert,
    AlertTitle,
    Collapse,
    IconButton,
    useMediaQuery
} from '@mui/material';
import {
    Error as ErrorIcon,
    Refresh,
    School as SchoolIcon,
    Warning,
    BugReport,
    CloudOff,
    ReportProblem,
    Science as ScienceIcon,
    Psychology as PsychologyIcon,
    MenuBook as MenuBookIcon,
    Analytics as AnalyticsIcon,
    Groups as GroupsIcon,
    Timeline as TimelineIcon,
    Lightbulb as LightbulbIcon,
    ConnectWithoutContact as ConnectIcon,
    Assessment as AssessmentIcon,
    Close as CloseIcon,
    Email as EmailIcon,
    SupportAgent as SupportIcon,
    Article as ArticleIcon,
    RestartAlt as RestartIcon,
    Home as HomeIcon,
    ArrowBack as ArrowBackIcon,
    Help
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ErrorState = ({ 
    error, 
    onRetry, 
    title = "Something went wrong",
    subTitle = "We couldn't complete your request",
    icon = null,
    variant = "default",
    fullPage = false,
    showRetry = true,
    showHomeButton = false,
    showBackButton = false,
    showSupport = true,
    retryText = "Try Again",
    errorCode = null,
    details = null,
    onHomeClick,
    onBackClick,
    onSupportClick
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [showDetails, setShowDetails] = React.useState(false);

    // Responsive sizes
    const getResponsiveSizes = () => {
        if (isMobile) {
            return {
                avatarSize: 100,
                iconFontSize: 60,
                titleFontSize: '1.5rem',
                subTitleFontSize: '1rem',
                bodyFontSize: '0.875rem',
                buttonSize: 'medium',
                buttonPadding: '0.75rem 1.5rem',
                chipSize: 'small',
                spacing: 2,
                paperPadding: 2,
                maxWidth: '95%'
            };
        }
        if (isTablet) {
            return {
                avatarSize: 120,
                iconFontSize: 70,
                titleFontSize: '1.75rem',
                subTitleFontSize: '1.125rem',
                bodyFontSize: '0.9375rem',
                buttonSize: 'large',
                buttonPadding: '1rem 2rem',
                chipSize: 'small',
                spacing: 2.5,
                paperPadding: 2.5,
                maxWidth: '90%'
            };
        }
        return {
            avatarSize: 140,
            iconFontSize: 80,
            titleFontSize: '2rem',
            subTitleFontSize: '1.25rem',
            bodyFontSize: '1rem',
            buttonSize: 'large',
            buttonPadding: '1.2rem 2.5rem',
            chipSize: 'medium',
            spacing: 3,
            paperPadding: 3,
            maxWidth: 600
        };
    };

    const responsive = getResponsiveSizes();

    // Error variants for different research scenarios with responsive icon sizes
    const errorVariants = {
        research: {
            icon: <ScienceIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.info.main,
            title: "Research Data Error",
            subTitle: "Unable to load research data",
            suggestion: "Check your internet connection and try again"
        },
        supervision: {
            icon: <GroupsIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.warning.main,
            title: "Supervision Error",
            subTitle: "Unable to load supervision details",
            suggestion: "Refresh the page or contact your supervisor"
        },
        publication: {
            icon: <MenuBookIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.secondary.main,
            title: "Publication Error",
            subTitle: "Unable to load publications",
            suggestion: "Try again later or check your permissions"
        },
        analytics: {
            icon: <AnalyticsIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.info.main,
            title: "Analytics Error",
            subTitle: "Unable to generate analytics",
            suggestion: "Try refreshing the page or contact support"
        },
        matching: {
            icon: <ConnectIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.primary.main,
            title: "Matching Error",
            subTitle: "Unable to find supervisor matches",
            suggestion: "Complete your profile for better matches"
        },
        workflow: {
            icon: <TimelineIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.warning.main,
            title: "Workflow Error",
            subTitle: "Unable to load workflow data",
            suggestion: "Refresh the page or check your internet connection"
        },
        ideation: {
            icon: <LightbulbIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.success.main,
            title: "Ideation Error",
            subTitle: "Unable to generate research ideas",
            suggestion: "Try different keywords or check your internet connection"
        },
        connection: {
            icon: <CloudOff sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.error.main,
            title: "Connection Error",
            subTitle: "Unable to connect to server",
            suggestion: "Check your internet connection and try again"
        },
        permission: {
            icon: <ReportProblem sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.warning.main,
            title: "Permission Denied",
            subTitle: "You don't have access to this resource",
            suggestion: "Contact your administrator for access"
        },
        server: {
            icon: <BugReport sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.error.main,
            title: "Server Error",
            subTitle: "Something went wrong on our end",
            suggestion: "Please try again later or contact support"
        },
        default: {
            icon: <ErrorIcon sx={{ fontSize: responsive.iconFontSize }} />,
            color: theme.palette.error.main,
            title: "Something went wrong",
            subTitle: "An unexpected error occurred",
            suggestion: "Try refreshing the page or contact support"
        }
    };

    const currentVariant = errorVariants[variant] || errorVariants.default;
    const finalIcon = icon || currentVariant.icon;
    const finalColor = currentVariant.color;
    const finalTitle = title !== "Something went wrong" ? title : currentVariant.title;
    const finalSubTitle = subTitle !== "We couldn't complete your request" ? subTitle : currentVariant.subTitle;

    // Get help articles based on error variant
    const getHelpArticles = () => {
        const articles = {
            research: [
                "How to submit research proposals",
                "Understanding research workflow",
                "Research data management guide"
            ],
            supervision: [
                "How to find a supervisor",
                "Supervision expectations guide",
                "Communication with supervisors"
            ],
            publication: [
                "Publication readiness checklist",
                "How to submit publications",
                "Publication guidelines"
            ],
            matching: [
                "How to improve supervisor matches",
                "Profile completion guide",
                "Research interest selection tips"
            ],
            default: [
                "Troubleshooting guide",
                "Contact support",
                "System requirements"
            ]
        };
        return articles[variant] || articles.default;
    };

    // Limit articles on mobile
    const helpArticles = getHelpArticles();
    const displayArticles = isMobile ? helpArticles.slice(0, 2) : helpArticles;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: fullPage ? '100vh' : (isMobile ? '300px' : '400px'),
                width: '100%',
                gap: responsive.spacing,
                p: isMobile ? 2 : 4,
                position: 'relative',
                background: fullPage ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)` : 'transparent'
            }}
        >
            {/* Decorative background pattern - hidden on mobile for performance */}
            {!isMobile && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.03,
                        zIndex: 0,
                        pointerEvents: 'none',
                        overflow: 'hidden'
                    }}
                >
                    <SchoolIcon sx={{ fontSize: 300, position: 'absolute', top: '10%', left: '-5%' }} />
                    <ScienceIcon sx={{ fontSize: 250, position: 'absolute', bottom: '10%', right: '-5%' }} />
                    <PsychologyIcon sx={{ fontSize: 200, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                </Box>
            )}

            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Animated error icon */}
                    <motion.div
                        animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, -5, 5, -5, 0]
                        }}
                        transition={{
                            duration: 0.5,
                            ease: "easeInOut"
                        }}
                    >
                        <Avatar
                            sx={{
                                width: responsive.avatarSize,
                                height: responsive.avatarSize,
                                background: `linear-gradient(135deg, ${finalColor}20, ${finalColor}10)`,
                                color: finalColor,
                                boxShadow: `0 8px 32px ${finalColor}40`,
                                border: `2px solid ${finalColor}30`
                            }}
                        >
                            {finalIcon}
                        </Avatar>
                    </motion.div>
                    
                    {/* Error badge */}
                    {errorCode && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: isMobile ? -5 : -10,
                                right: isMobile ? -5 : -10,
                                px: isMobile ? 1 : 1.5,
                                py: isMobile ? 0.25 : 0.5,
                                borderRadius: 20,
                                backgroundColor: finalColor,
                                color: 'white',
                                fontSize: isMobile ? '0.65rem' : '0.75rem',
                                fontWeight: 'bold',
                                fontFamily: 'monospace'
                            }}
                        >
                            {errorCode}
                        </Box>
                    )}
                </Box>
            </Zoom>
            
            <Fade in={true} timeout={800}>
                <Box sx={{ textAlign: 'center', maxWidth: responsive.maxWidth, width: '100%', zIndex: 1 }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            color: finalColor,
                            fontWeight: 700,
                            mb: 1,
                            letterSpacing: '-0.5px',
                            fontSize: responsive.titleFontSize
                        }}
                    >
                        {finalTitle}
                    </Typography>
                    
                    <Typography 
                        variant="h6"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            mb: 2,
                            fontWeight: 500,
                            fontSize: responsive.subTitleFontSize
                        }}
                    >
                        {finalSubTitle}
                    </Typography>
                    
                    <Paper
                        elevation={0}
                        sx={{
                            p: responsive.paperPadding,
                            bgcolor: `${finalColor}08`,
                            borderRadius: isMobile ? 2 : 3,
                            border: `1px solid ${finalColor}30`,
                            mb: 3
                        }}
                    >
                        <Stack spacing={responsive.spacing}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <ErrorIcon sx={{ color: finalColor, fontSize: isMobile ? 20 : 24 }} />
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: finalColor,
                                        fontWeight: 500,
                                        wordBreak: 'break-word',
                                        fontSize: responsive.bodyFontSize
                                    }}
                                >
                                    {error || currentVariant.suggestion}
                                </Typography>
                            </Box>
                            
                            {details && (
                                <>
                                    <Divider sx={{ borderColor: `${finalColor}30` }} />
                                    <Box>
                                        <Button
                                            size="small"
                                            onClick={() => setShowDetails(!showDetails)}
                                            endIcon={showDetails ? <CloseIcon /> : <Help />}
                                            sx={{ color: finalColor }}
                                        >
                                            {showDetails ? 'Hide Details' : 'Show Error Details'}
                                        </Button>
                                        <Collapse in={showDetails}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    mt: 2,
                                                    p: isMobile ? 1 : 2,
                                                    bgcolor: `${finalColor}05`,
                                                    borderColor: `${finalColor}30`,
                                                    fontFamily: 'monospace',
                                                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                                                    overflowX: 'auto'
                                                }}
                                            >
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                                    {typeof details === 'object' ? JSON.stringify(details, null, 2) : details}
                                                </pre>
                                            </Paper>
                                        </Collapse>
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </Paper>

                    {/* Help Articles Section */}
                    {showSupport && displayArticles.length > 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: responsive.paperPadding,
                                mb: 3,
                                borderRadius: isMobile ? 2 : 3,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
                                border: `1px solid ${theme.palette.primary.main}20`
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ 
                                mb: 1, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: responsive.bodyFontSize
                            }}>
                                <ArticleIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                                Helpful Resources
                            </Typography>
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={1} 
                                flexWrap="wrap" 
                                useFlexGap
                                alignItems="center"
                            >
                                {displayArticles.map((article, index) => (
                                    <Chip
                                        key={index}
                                        label={article}
                                        size={responsive.chipSize}
                                        variant="outlined"
                                        sx={{ mb: 1, width: { xs: '100%', sm: 'auto' } }}
                                        onClick={onSupportClick}
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    )}
                    
                    {/* Action Buttons */}
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        justifyContent="center" 
                        flexWrap="wrap" 
                        useFlexGap
                    >
                        {showBackButton && onBackClick && (
                            <Button
                                variant="outlined"
                                size={responsive.buttonSize}
                                startIcon={<ArrowBackIcon />}
                                onClick={onBackClick}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 2,
                                    px: isMobile ? 2 : 3,
                                    py: isMobile ? 1 : 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: finalColor,
                                    color: finalColor,
                                    '&:hover': {
                                        borderColor: finalColor,
                                        backgroundColor: `${finalColor}10`,
                                        transform: isMobile ? 'none' : 'translateY(-2px)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Go Back
                            </Button>
                        )}
                        
                        {showHomeButton && onHomeClick && (
                            <Button
                                variant="outlined"
                                size={responsive.buttonSize}
                                startIcon={<HomeIcon />}
                                onClick={onHomeClick}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 2,
                                    px: isMobile ? 2 : 3,
                                    py: isMobile ? 1 : 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: `${theme.palette.primary.main}10`,
                                        transform: isMobile ? 'none' : 'translateY(-2px)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Go Home
                            </Button>
                        )}
                        
                        {showRetry && onRetry && (
                            <Button
                                variant="contained"
                                size={responsive.buttonSize}
                                startIcon={<Refresh />}
                                onClick={onRetry}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 2,
                                    px: isMobile ? 3 : 4,
                                    py: isMobile ? 1 : 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: `linear-gradient(135deg, ${finalColor}, ${finalColor}dd)`,
                                    boxShadow: `0 8px 16px ${finalColor}40`,
                                    '&:hover': {
                                        transform: isMobile ? 'none' : 'translateY(-2px)',
                                        boxShadow: `0 12px 24px ${finalColor}60`,
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {retryText}
                            </Button>
                        )}
                    </Stack>

                    {/* Support Contact */}
                    {showSupport && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                Need help?{' '}
                                <Button
                                    size="small"
                                    startIcon={<SupportIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                    onClick={onSupportClick}
                                    sx={{ textTransform: 'none', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                >
                                    Contact Support
                                </Button>
                                {' or '}
                                <Button
                                    size="small"
                                    startIcon={<EmailIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                                    onClick={onSupportClick}
                                    sx={{ textTransform: 'none', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                                >
                                    Email Us
                                </Button>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Fade>
        </Box>
    );
};

export default ErrorState;
// Basic error state
{/* <ErrorState 
    error="Failed to load research data" 
    onRetry={fetchResearchData}
/>

// Full page error with specific variant
<ErrorState 
    variant="connection"
    fullPage={true}
    onRetry={reconnect}
    errorCode="ERR_CONNECTION_REFUSED"
/>

// Permission error with details
<ErrorState 
    variant="permission"
    title="Access Denied"
    subTitle="You don't have permission to view this research"
    error="You need supervisor approval to access this resource"
    details={{ required_role: "supervisor", user_role: "student" }}
    showHomeButton={true}
    onHomeClick={() => navigate('/dashboard')}
/>

// Matching error with help articles
<ErrorState 
    variant="matching"
    error="No supervisor matches found"
    onRetry={retryMatching}
    showSupport={true}
    onSupportClick={() => openSupportModal()}
/>

// Server error with back button
<ErrorState 
    variant="server"
    errorCode="500"
    showBackButton={true}
    onBackClick={() => navigate(-1)}
    showHomeButton={true}
    onHomeClick={() => navigate('/')}
/>

// Research data error with custom retry
<ErrorState 
    variant="research"
    title="Research Data Unavailable"
    error="Unable to fetch your research proposals"
    onRetry={fetchResearchProposals}
    retryText="Retry Loading"
    details={{ endpoint: "/api/research", timestamp: new Date().toISOString() }}
/>

// Supervision error
<ErrorState 
    variant="supervision"
    error="Supervisor assignment data not found"
    onRetry={refreshSupervision}
    showHomeButton={true}
/>

// Analytics error
<ErrorState 
    variant="analytics"
    error="Failed to generate research analytics"
    onRetry={loadAnalytics}
    showBackButton={true}
/>

// Publication error
<ErrorState 
    variant="publication"
    error="Unable to load publication list"
    onRetry={fetchPublications}
    errorCode="PUB-404"
/>

// Workflow error
<ErrorState 
    variant="workflow"
    error="Research workflow data corrupted"
    onRetry={resetWorkflow}
    details={{ workflow_id: "WF-12345", step: "submission" }}
/>

// Ideation error
<ErrorState 
    variant="ideation"
    error="Failed to generate research ideas"
    onRetry={regenerateIdeas}
    showSupport={true}
/> */}

